/**
 * Stufen-Zuordnung des Quality-Score-Badges in der Werkbank-Liste
 * (Design-Audit F15).
 *
 * Vorher kannte der Badge drei Zustaende, und alles ab 70 trug dieselbe
 * deckende gruene Flaeche: 72 sah aus wie 100. Gleichzeitig faerbt die
 * Feed-Note eine 64 orange -- ein Produkt mit 72 stand also im selben Gruen
 * wie ein perfektes, waehrend derselbe Zahlenbereich eine Ebene hoeher als
 * Warnung galt.
 *
 * Die vierte Stufe "solide" (70-84) schliesst genau diesen Widerspruch. Sie
 * kommt aus den Neutrals statt aus einem vierten Buntton: "solide" ist ein
 * Zwischenzustand ohne Qualitaetsurteil, und ein vierter Signalton haette die
 * dreifarbige Ampel entwertet.
 *
 * WICHTIG -- die Schwellen der Filterpillen (40 / 70) bleiben unberuehrt. Die
 * vierte Stufe ist rein visuell: sie teilt "Gut" optisch, aber sie teilt keinen
 * Filter. Wer hier eine Filterschwelle bei 85 ergaenzt, aendert eine gemessene
 * Auswahl und nicht nur eine Farbe.
 *
 * Eigenes Modul statt einer Hilfsfunktion im Komponentenrumpf, damit
 * `scoreBadgeContrast.test.ts` die echte Funktion aufrufen kann statt den
 * Quelltext mit einer Regex abzutasten -- eine Regex sagt nur, wie der Code
 * aussieht, nicht was er tut.
 */

/** Die Badge-Varianten, die dieser Badge benutzen darf. Siehe `ui/badge.tsx`. */
export type ScoreBadgeVariant =
  | 'scoreCritical'
  | 'scoreWarning'
  | 'scoreSolid'
  | 'scoreExcellent'
  | 'secondary';

/**
 * Untere Grenzen der vier Stufen, jeweils einschliesslich.
 *
 * `critical` beginnt bei 0 und braucht deshalb keinen Eintrag. `excellent`
 * deckt sich mit der Note A des Feed-Checks (`gradeForScore`), `solid` mit
 * Note B -- die Produktebene faerbt damit nach denselben Grenzen wie die
 * Feed-Ebene.
 */
export const SCORE_BADGE_THRESHOLDS = {
  warning: 40,
  solid: 70,
  excellent: 85,
} as const;

/**
 * Ungemessen ist keine Stufe. `null` heisst "noch kein Score", nicht "Score 0";
 * eine der vier Signalstufen daraufzusetzen waere eine erfundene Aussage.
 */
export function scoreBadgeVariant(score: number | null): ScoreBadgeVariant {
  if (score === null) return 'secondary';
  if (score < SCORE_BADGE_THRESHOLDS.warning) return 'scoreCritical';
  if (score < SCORE_BADGE_THRESHOLDS.solid) return 'scoreWarning';
  if (score < SCORE_BADGE_THRESHOLDS.excellent) return 'scoreSolid';
  return 'scoreExcellent';
}

/** Die vier gemessenen Stufen ohne den Ungemessen-Fall -- fuer Tests und Legenden. */
export const SCORE_BADGE_SCORED_VARIANTS = [
  'scoreCritical',
  'scoreWarning',
  'scoreSolid',
  'scoreExcellent',
] as const satisfies readonly ScoreBadgeVariant[];
